import { useLanguage } from "@/context/LanguageContext";
import SectionLabel from "@/components/SectionLabel";
import {
  Eye,
  Check,
  Mail,
  Database,
  FileText,
  Calendar,
  MessageSquare,
  Receipt,
} from "lucide-react";

const TOOL_ICONS = {
  Gmail: Mail,
  CRM: Database,
  Drive: FileText,
  Calendar: Calendar,
  Slack: MessageSquare,
  Facturation: Receipt,
  Billing: Receipt,
};

// Premium, well-spaced positions for the 6 floating windows
const BEFORE_POS = [
  { top: "4%", left: "4%", rotate: "-1.5deg" },
  { top: "8%", right: "6%", rotate: "1.2deg" },
  { top: "38%", left: "10%", rotate: "0.8deg" },
  { top: "42%", right: "2%", rotate: "-0.8deg" },
  { top: "72%", left: "2%", rotate: "-0.6deg" },
  { top: "76%", right: "10%", rotate: "1.5deg" },
];

function WindowCard({ tool, note, pos, idx }) {
  const Icon = TOOL_ICONS[tool] || FileText;
  return (
    <div
      className="absolute w-[195px] opacity-0"
      style={{
        ...pos,
        transform: `rotate(${pos.rotate})`,
        animation: `floatIn 0.9s ease-out ${idx * 0.08}s forwards`,
      }}
    >
      <div className="rounded-xl bg-[#1A1814] border border-white/10 shadow-[0_12px_28px_-12px_rgba(0,0,0,0.7)] overflow-hidden hover:border-white/20 transition-colors">
        {/* Window chrome */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-white/[0.07]">
          <span className="w-1.5 h-1.5 rounded-full bg-white/15" />
          <span className="w-1.5 h-1.5 rounded-full bg-white/15" />
          <span className="w-1.5 h-1.5 rounded-full bg-white/15" />
        </div>
        <div className="px-3.5 py-3 flex items-start gap-2.5">
          <div className="shrink-0 w-7 h-7 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center text-[#8F9FFE]">
            <Icon size={13} strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-white/45 mb-0.5">
              {tool}
            </div>
            <div className="text-[12px] text-white/85 leading-snug truncate">{note}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Constat() {
  const { t, locale } = useLanguage();
  const tools = t("constat.tools");
  const actionItems = t("constat.action_items");
  const conversation = t("constat.conversation");

  return (
    <section
      id="constat"
      data-testid="constat-section"
      className="relative overflow-hidden bg-[#1A1814] text-white"
    >
      {/* Soft halo */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] opacity-25"
          style={{
            background:
              "radial-gradient(circle, rgba(67,125,234,0.45) 0%, transparent 70%)",
            filter: "blur(80px)",
          }}
        />
      </div>

      <div className="container-iko relative py-24 lg:py-32">
        {/* Editorial intro */}
        <div className="max-w-3xl mb-14 lg:mb-16">
          <SectionLabel icon={Eye} onDark>{t("constat.eyebrow")}</SectionLabel>
          <h2 className="font-display font-semibold text-4xl lg:text-[58px] leading-[1] tracking-[-0.034em] text-balance mb-6">
            <span className="block">{t("constat.title_l1")}</span>
            <span className="block text-white/55">{t("constat.title_l2")}</span>
          </h2>
          <p className="text-base lg:text-lg text-white/70 leading-relaxed max-w-2xl">
            {t("constat.subtitle")}
          </p>
        </div>

        {/* Central composition: 3 zones */}
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-0 items-stretch">
          {/* AVANT — left panel */}
          <div className="lg:col-span-6 relative">
            <div className="flex items-center gap-3 mb-5">
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] font-semibold text-white/45">
                {t("constat.before_label")}
              </span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <div
              data-testid="constat-before"
              className="relative h-[480px] lg:h-[540px] rounded-3xl bg-[#1A1814] border border-white/[0.08] overflow-hidden"
            >
              {/* subtle grid */}
              <div
                className="absolute inset-0 opacity-[0.045]"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                  backgroundSize: "32px 32px",
                }}
              />
              {/* Floating windows */}
              {tools.slice(0, 6).map((tool, i) => (
                <WindowCard
                  key={`${locale}-${tool.name}`}
                  tool={tool.name}
                  note={tool.note}
                  pos={BEFORE_POS[i]}
                  idx={i}
                />
              ))}

              {/* Fine lines emerging from windows (SVG overlay) */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                aria-hidden
              >
                <defs>
                  <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="rgba(67,125,234,0)" />
                    <stop offset="100%" stopColor="rgba(67,125,234,0.45)" />
                  </linearGradient>
                </defs>
                {/* 6 thin lines toward right edge */}
                {[
                  { y1: "12%", y2: "50%" },
                  { y1: "18%", y2: "50%" },
                  { y1: "46%", y2: "50%" },
                  { y1: "50%", y2: "50%" },
                  { y1: "80%", y2: "50%" },
                  { y1: "85%", y2: "50%" },
                ].map((l, i) => (
                  <line
                    key={i}
                    x1="60%"
                    y1={l.y1}
                    x2="100%"
                    y2={l.y2}
                    stroke="url(#line-grad)"
                    strokeWidth="1"
                    strokeDasharray="3 4"
                    opacity="0.6"
                    style={{
                      animation: `lineDraw 1.2s ease-out ${0.5 + i * 0.1}s both`,
                    }}
                  />
                ))}
              </svg>
            </div>
          </div>

          {/* CENTRE — small luminous iko orb */}
          <div className="hidden lg:flex lg:col-span-0 absolute left-1/2 top-[55%] -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
            <div className="relative">
              <div
                className="absolute -inset-8 rounded-full opacity-70"
                style={{
                  background:
                    "radial-gradient(circle, rgba(67,125,234,0.7) 0%, transparent 65%)",
                  filter: "blur(18px)",
                  animation: "ikoPulse 3s ease-in-out infinite",
                }}
              />
              <div
                className="relative w-14 h-14 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-[0_0_30px_rgba(67,125,234,0.7)]"
              >
                <img src="/img/iko-mark.png" alt="iko" className="w-full h-full object-contain" />
              </div>
            </div>
          </div>

          {/* APRÈS — right panel */}
          <div className="lg:col-span-6 relative flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px bg-[#8F9FFE]/20" />
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] font-semibold text-[#8F9FFE]">
                {t("constat.after_label")}
              </span>
            </div>
            <div
              data-testid="constat-after"
              className="relative flex-1 rounded-3xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/[0.12] p-5 lg:p-6 backdrop-blur min-h-[480px] lg:min-h-[540px] flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center gap-2.5 pb-4 border-b border-white/[0.08] mb-4">
                <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center overflow-hidden border border-white/20">
                  <img src="/img/iko-mark.png" alt="iko" className="w-full h-full object-contain" />
                </div>
                <span className="font-display font-bold text-[13px] tracking-tight lowercase">
                  iko
                </span>
                <div className="flex items-center gap-1.5 ml-auto">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
                  <span className="font-mono text-[9px] uppercase tracking-wider text-white/45">
                    {locale === "fr" ? "en ligne" : "online"}
                  </span>
                </div>
              </div>

              {/* User bubble */}
              <div className="flex justify-end mb-3">
                <div className="max-w-[85%] px-3.5 py-2 bg-[#437DEA] text-white text-[13px] leading-snug rounded-2xl rounded-br-md shadow-[0_4px_14px_-4px_rgba(67,125,234,0.5)]">
                  {conversation.user}
                </div>
              </div>

              {/* iko bubble */}
              <div className="flex justify-start mb-5">
                <div className="max-w-[88%] px-3.5 py-2 bg-white/[0.08] border border-white/10 text-white text-[13px] leading-snug rounded-2xl rounded-bl-md">
                  {conversation.ai}
                </div>
              </div>

              {/* Result card */}
              <div className="mt-auto rounded-2xl bg-white/[0.04] border border-white/10 p-4">
                <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#8F9FFE] font-semibold mb-3">
                  {locale === "fr" ? "Résultat" : "Result"}
                </div>
                <ul className="space-y-2">
                  {actionItems.map((a, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-2.5 text-[13px] text-white"
                    >
                      <div className="shrink-0 w-4 h-4 rounded-full bg-[#437DEA] flex items-center justify-center">
                        <Check size={9} strokeWidth={3} className="text-white" />
                      </div>
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Centered hero phrase */}
        <div className="mt-20 lg:mt-24 pt-12 border-t border-white/10 text-center max-w-4xl mx-auto">
          <p className="text-[15px] lg:text-base text-white/55 leading-relaxed mb-6">
            {t("constat.hero_phrase_l1")}
          </p>
          <p className="font-display font-semibold text-3xl lg:text-[44px] leading-[1.1] tracking-[-0.028em] text-balance text-white">
            {t("constat.hero_phrase_l3")}
          </p>
        </div>
      </div>

      <style>{`
        @keyframes floatIn {
          from { opacity: 0; transform: translateY(12px) rotate(0deg); }
          to { opacity: 1; }
        }
        @keyframes lineDraw {
          from { opacity: 0; stroke-dashoffset: 200; }
          to { opacity: 0.6; stroke-dashoffset: 0; }
        }
        @keyframes ikoPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.08); }
        }
      `}</style>
    </section>
  );
}
